import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Tile from "./Tile";
import Card from "../../theme/untitled/Card/Card";
import { IT } from "../../../../consts";
import "./WelcomeScreen.css";

// Matches the CSS transition duration on .cfg-tile/.cfg-mark/.cfg-title —
// keep these in sync, see WelcomeScreen.css.
const HIGHLIGHT_MS = 500;

const PLATFORM_TM = {
  [IT.google]: "Google Calendar™",
  [IT.outlook]: "Outlook Calendar™",
};

const PLATFORM_PLAIN = {
  [IT.google]: "Google Calendar",
  [IT.outlook]: "Outlook Calendar",
};

const OTHER_PLATFORM = {
  [IT.google]: IT.outlook,
  [IT.outlook]: IT.google,
};

/**
 * "Connect a calendar platform" is deliberately generic — both Google and
 * Outlook are offered — so the explanation names whichever is actually
 * connected instead of assuming Google.
 */
function getPlatformExplanation(connectedIntegrationTypes) {
  const connected = connectedIntegrationTypes.find((type) => PLATFORM_TM[type]);

  if (!connected) {
    return "Google Calendar™ connected. Outlook Calendar is also available.";
  }

  return `${PLATFORM_TM[connected]} connected. ${
    PLATFORM_PLAIN[OTHER_PLATFORM[connected]]
  } is also available.`;
}

const WelcomeScreen = (props) => {
  const {
    isActivated,
    hasSync,
    hasCalendar,
    hasWebsite,
    connectedIntegrationTypes = [],
  } = props;

  const allRequiredDone = isActivated && hasSync && hasCalendar;

  // Staggered reveal: "Activate account" and "Connect a calendar platform"
  // are both already known synchronously (session/account data) by the time
  // this mounts, so without gating they'd render as done on the very first
  // frame — no highlight to see. Holding each behind its own timer lets the
  // done state flip in on a beat (0–0.5s, then 0.5–1s) instead of all at
  // once. "Create your first widget" needs no gate — hasCalendar depends on
  // an async fetch, so it's already naturally staggered; it flips whenever
  // the request resolves, using the same CSS transition.
  const [activateGateOpen, setActivateGateOpen] = useState(false);
  const [syncGateOpen, setSyncGateOpen] = useState(false);

  useEffect(() => {
    setActivateGateOpen(true);
    const syncTimer = setTimeout(() => setSyncGateOpen(true), HIGHLIGHT_MS);
    return () => clearTimeout(syncTimer);
  }, []);

  // Once every required step is genuinely done, keep the card mounted for
  // one more highlight beat so the last checkmark's transition is visible,
  // then hide it. If it's already fully done on mount (e.g. a returning,
  // fully-onboarded user), hide immediately with no gratuitous replay.
  const [hidden, setHidden] = useState(allRequiredDone);

  useEffect(() => {
    if (!allRequiredDone) {
      setHidden(false);
      return;
    }
    const hideTimer = setTimeout(() => setHidden(true), HIGHLIGHT_MS + 100);
    return () => clearTimeout(hideTimer);
  }, [allRequiredDone]);

  if (hidden) {
    return null;
  }

  const visualActivated = activateGateOpen && isActivated;
  const visualSync = syncGateOpen && hasSync;

  // Which tile gets the single primary button is based on the real
  // (ungated) completion state, so it doesn't jump around while the
  // reveal timers are still running.
  const requiredSteps = [
    { key: "activate", completed: Boolean(isActivated) },
    { key: "sync", completed: hasSync },
    { key: "widget", completed: hasCalendar },
  ];
  const nextRequiredKey = requiredSteps.find((s) => !s.completed)?.key;

  // The header count follows the gated/visual completion instead, so it
  // ticks up in step with the tiles highlighting rather than jumping
  // straight to its final value.
  const doneCount = [visualActivated, visualSync, hasCalendar].filter(
    Boolean
  ).length;

  const stateFor = (key, visualCompleted, optional = false) => {
    if (visualCompleted) return "done";
    if (!optional && key === nextRequiredKey) return "next";
    return "not-started";
  };

  const activateState = stateFor("activate", visualActivated);
  const syncState = stateFor("sync", visualSync);
  const widgetState = stateFor("widget", hasCalendar);
  const websiteState = stateFor("website", hasWebsite, true);

  return (
    <Card>
      <Card.Header
        title="Configuration checklist"
        subtitle="A few things to set up before you can share your first widget."
      >
        <span className="cfg-header-count">{doneCount} of 3 done</span>
      </Card.Header>
      <Card.Body noPadding>
        <div className="cfg-tiles">
          <Tile
            number={1}
            state={activateState}
            title="Activate account"
            explanation={
              isActivated
                ? "Email confirmed, so we can sync calendars on your behalf."
                : "Confirms your email so we can sync calendars on your behalf."
            }
            ctaLabel="Check your inbox to confirm"
          />
          <Tile
            number={2}
            state={syncState}
            title="Connect a data source"
            explanation={getPlatformExplanation(connectedIntegrationTypes)}
            linkUrl={"/integrations"}
            ctaLabel="Connect a platform"
          />
          <Tile
            number={3}
            state={widgetState}
            title="Create your first widget"
            explanation="Choose a layout and which calendars feed it. Nothing is live until a widget exists."
            linkUrl={"/calendar/new"}
            ctaLabel={widgetState === "next" ? "Create widget" : "Create a widget"}
          />
          <Tile
            number={4}
            state={websiteState}
            title="Grant widget access to your websites"
            explanation="Only needed to embed a widget on your own site. You can share a widget without this."
            linkUrl={"/domains"}
            ctaLabel="Add a website"
            optional
          />
        </div>
      </Card.Body>
    </Card>
  );
};

WelcomeScreen.propTypes = {
  isActivated: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  hasSync: PropTypes.bool,
  hasCalendar: PropTypes.bool,
  hasWebsite: PropTypes.bool,
  connectedIntegrationTypes: PropTypes.arrayOf(PropTypes.string),
};

export default WelcomeScreen;
